import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Layout } from "../../components/Layout";
import { PageHeader } from "../../components/PageHeader";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Chip } from "../../components/ui/chip";
import { IconToggle } from "../../components/ui/icon-toggle";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { useLang } from "../../contexts/LangContext";
import { tx } from "../../i18n";
import { formatMoney } from "../../lib/api";
import { LineItemsView } from "../../components/LineItemsView";
import { chatService } from "../../services/chatService";
import { profileService } from "../../services/profileService";
import { pushHighlight } from "../../lib/highlights";
import { toast } from "sonner";
import {
  Sparkles, Send, Loader2, Trash2, CheckCircle2, Pencil, AlertCircle, AlertTriangle, Bot, User, MessageSquareDashed, MessagesSquare, Paperclip,
} from "lucide-react";
import { Link } from "react-router-dom";

const SUGGESTIONS_EN = [
  "Client Ana paid me 800000 for design",
  "I paid 120000 for internet",
  "Client Pedro owes 500000, due this Friday",
  "I need to pay 250000 for software on Monday",
];
const SUGGESTIONS_ES = [
  "Cliente Ana me pagó 800000 por diseño",
  "Pagué 120000 de internet",
  "Cliente Pedro me debe 500000, vence el viernes",
  "Tengo que pagar 250000 de software el lunes",
];

// Generate a stable client-side id for chat messages (used as React key).
const newMsgId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID) ? crypto.randomUUID() : `m-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export default function QuickAdd() {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]); // {role, content}
  const [pendingItems, setPendingItems] = useState([]); // accumulated parsed items
  // iter-169zv — warnings keyed by the *current* index in pendingItems.
  // We re-base them when the user removes an item so the inline alert
  // stays attached to the right row.
  const [duplicateWarnings, setDuplicateWarnings] = useState({});
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [currency, setCurrency] = useState("USD");
  const endRef = useRef(null);

  useEffect(() => {
    profileService.get().then((p) => p?.currency && setCurrency(p.currency)).catch((e) => console.warn("profile load failed", e));
    // Load existing chat history (memory) — preserves quick_replies if the
    // last assistant turn was asking for disambiguation.
    chatService.history(50).then((data) => {
      if (Array.isArray(data) && data.length) {
        setMessages(data.map((m, i) => ({
          id: m.id || `srv-${m.role}-${i}`,
          role: m.role,
          content: m.content,
          quick_replies: (i === data.length - 1 && m.role === "assistant") ? (m.extra?.quick_replies || []) : [],
        })));
      }
    }).catch((e) => console.warn("chat history load failed", e));
  }, []);

  useEffect(() => {
    if (messages.length === 0) {
      const greet = tx(lang,
        "Hi! Tell me what movement you want to log. For example: 'Client Ana paid me 800000 for design' or 'I paid 120000 for internet'.",
        "¡Hola! Cuéntame qué movimiento quieres registrar. Por ejemplo: 'Cliente Ana me pagó 800000 por diseño' o 'pagué 120000 de internet'.",
        "Olá! Conta o que aconteceu para eu registrar. Por exemplo: 'Cliente Ana me pagou 800000 por design' ou 'paguei 120000 de internet'.",
      );
      // eslint-disable-next-line
      setMessages([{ id: newMsgId(), role: "assistant", content: greet, ephemeral: true }]);
    }
  }, [lang]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, pendingItems]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setSending(true);
    setInput("");
    // Strip quick_replies from earlier turns — once the user answers, the chips
    // shouldn't keep dangling under the previous assistant message.
    setMessages((m) => [
      ...m.filter((x) => !x.ephemeral).map((x) => x.quick_replies ? { ...x, quick_replies: [] } : x),
      { id: newMsgId(), role: "user", content: msg },
    ]);
    try {
      const data = await chatService.send(msg);
      setMessages((m) => [...m, {
        id: newMsgId(),
        role: "assistant",
        content: data.reply || "OK",
        quick_replies: Array.isArray(data.quick_replies) ? data.quick_replies : [],
      }]);
      if (Array.isArray(data.items) && data.items.length) {
        setPendingItems((arr) => [...arr, ...data.items]);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message;
      setMessages((m) => [...m, { id: newMsgId(), role: "assistant", content: `⚠️ ${detail}`, error: true }]);
    } finally {
      setSending(false);
    }
  };

  const clearChat = async () => {
    try {
      await chatService.clearHistory();
      setMessages([]);
      setPendingItems([]);
      setDuplicateWarnings({});
      toast.success(tx(lang, "Chat cleared", "Conversación borrada", "Conversa apagada"));
    } catch (e) {
      console.warn("clear chat failed", e);
      toast.error("Could not clear chat");
    }
  };

  // iter-169s — Receipt OCR: upload image → Claude vision → parsed items
  const fileInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  // iter-169y — the OCR endpoint always persists the receipt in GridFS;
  // when the user has to manually confirm extracted items, we forward
  // this pending id so the linked attachment doc gets updated with the
  // saved movement ids on confirm.
  const [pendingAttachmentId, setPendingAttachmentId] = useState(null);

  const onImagePicked = async (file) => {
    if (!file) return;
    const valid = ["image/png", "image/jpeg", "image/jpg", "image/webp", "application/pdf"];
    if (!valid.includes(file.type)) {
      toast.error(tx(lang, "Only PNG, JPG, WEBP or PDF", "Sólo PNG, JPG, WEBP o PDF", "Apenas PNG, JPG, WEBP ou PDF"));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(tx(lang, "Max 8 MB", "Máximo 8 MB", "Máximo 8 MB"));
      return;
    }
    setUploadingImage(true);
    setMessages((m) => [...m, {
      id: newMsgId(), role: "user",
      content: tx(lang, `📎 Uploaded: ${file.name}`, `📎 Subiste: ${file.name}`, `📎 Enviado: ${file.name}`),
    }]);
    try {
      const data = await chatService.extractImage(file);
      const count = (data.items || []).length;
      const autoSaved = !!data.auto_saved;
      const replyEs = count === 0
        ? "No pude extraer ningún movimiento de esta imagen. Prueba con otra foto más clara o con menos elementos."
        : autoSaved
        ? `✅ Guardé ${count} movimiento${count > 1 ? "s" : ""} automáticamente (confianza alta). Si algo no quedó bien, puedes editarlo desde Movimientos.` // design-system: ALLOW user-copy
        : `Detecté ${count} movimiento${count > 1 ? "s" : ""} en el comprobante. Revísalo${count > 1 ? "s" : ""} abajo y confirma para guardar.`;
      const replyEn = count === 0
        ? "Couldn't extract any movement from this image. Try a clearer photo or one with fewer elements."
        : autoSaved
        ? `✅ Auto-saved ${count} movement${count > 1 ? "s" : ""} (high confidence). Edit later from Movements if needed.` // design-system: ALLOW user-copy
        : `Found ${count} movement${count > 1 ? "s" : ""} in the receipt. Review below and confirm to save.`;
      setMessages((m) => [...m, {
        id: newMsgId(), role: "assistant",
        content: tx(lang, replyEn, replyEs, replyEs),
      }]);
      if (!autoSaved && Array.isArray(data.items) && data.items.length) {
        // iter-169zv — Merge new pending items + their duplicate
        // warnings keyed by their final index in the accumulated list.
        setPendingItems((arr) => {
          const baseLen = arr.length;
          // Re-key warnings to absolute indices in the merged array.
          if (Array.isArray(data.duplicate_warnings) && data.duplicate_warnings.length) {
            setDuplicateWarnings((prev) => {
              const next = { ...prev };
              for (const w of data.duplicate_warnings) {
                next[baseLen + w.index] = w;
              }
              return next;
            });
          }
          return [...arr, ...data.items];
        });
        if (data.attachment?.id) setPendingAttachmentId(data.attachment.id);
      }

      // iter-169z3 — Transparent pack debit toast. Surfaces immediately
      // after the OCR call so the user sees the unit count drop in real
      // time instead of having to refresh MyPlan to discover it. We
      // only toast when the backend actually charged a unit (data.remaining
      // is defined → a metered feature was billed).
      // iter-169zt — On cache_hit the bytes matched a recent upload and
      // we skipped Claude entirely: no unit consumed, distinct toast
      // so the user understands why "remaining" didn't change.
      if (data.cache_hit) {
        toast.success(
          tx(lang,
            "Reused previous analysis · no AI cost",
            "Reusé el análisis previo · sin costo de IA",
            "Reusei a análise anterior · sem custo de IA",
          ),
        );
      } else if (typeof data.remaining === "number") {
        const r = data.remaining;
        toast.success(
          tx(lang,
            `Used 1 AI receipt · ${r} left`,
            `Usaste 1 recibo IA · te quedan ${r}`,
            `Usou 1 recibo IA · restam ${r}`,
          ),
        );
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      let msg = e.message;
      if (detail && typeof detail === "object" && detail.code === "no_image_extractions") {
        msg = tx(lang,
          "No image-extraction credits. Buy the pack from MyPlan to continue.",
          "Sin saldo para comprobantes IA. Compra el pack desde MyPlan para seguir.",
          "Sem saldo para comprovantes IA. Compre o pacote em MyPlan para continuar.",
        );
      } else if (typeof detail === "string") {
        msg = detail;
      }
      setMessages((m) => [...m, {
        id: newMsgId(), role: "assistant",
        content: `⚠️ ${msg}`, error: true,
      }]);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const editItem = (idx, patch) => {
    setPendingItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  };
  const removeItem = (idx) => {
    setPendingItems((arr) => arr.filter((_, i) => i !== idx));
    // iter-169zv — shift duplicate warnings so the keys stay aligned
    // with the new array indices.
    setDuplicateWarnings((prev) => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const key = Number(k);
        if (key < idx) next[key] = v;
        else if (key > idx) next[key - 1] = v;
        // key === idx is dropped (item removed)
      });
      return next;
    });
  };
  const discardAllItems = () => {
    setPendingItems([]);
    setPendingAttachmentId(null);
    setDuplicateWarnings({});
  };

  // Cycle item type: income↔expense, receivable↔payment.
  // Reclassification mistakes by the AI are usually within one of these pairs.
  const swapItemType = (idx) => {
    const swaps = { income: "expense", expense: "income", receivable: "payment", payment: "receivable" };
    setPendingItems((arr) =>
      arr.map((it, i) => {
        if (i !== idx) return it;
        const next = swaps[it.type];
        if (!next) return it;
        // Carry-over fields where possible so the user doesn't lose what they typed.
        const carry = { ...it, type: next };
        if (it.type === "receivable" && next === "payment") {
          carry.concept = carry.concept || it.client_name || "";
          carry.due_date = carry.due_date || it.expected_date || "";
        }
        if (it.type === "payment" && next === "receivable") {
          carry.client_name = carry.client_name || it.concept || "";
          carry.expected_date = carry.expected_date || it.due_date || "";
        }
        return carry;
      })
    );
    toast.success(tx(lang, "Type changed", "Tipo cambiado", "Tipo alterado"));
  };

  const saveAll = async () => {
    if (!pendingItems.length) return;
    setSaving(true);
    try {
      const data = await chatService.confirm(pendingItems, pendingAttachmentId);
      const counts = (data.saved?.movements?.length || 0) + (data.saved?.receivables?.length || 0) + (data.saved?.payments?.length || 0);
      // Flash highlights for every freshly saved record so the receiving
      // page (whichever the user lands on) shows them glowing.
      const saved = data.saved || {};
      pushHighlight("movement", (saved.movements || []).map((m) => m.id));
      pushHighlight("payment", (saved.payments || []).map((p) => p.id));
      pushHighlight("receivable", (saved.receivables || []).map((r) => r.id));
      // mark_paid produced an expense movement — highlight by source_payment_id
      // so /history will glow the matching row.
      pushHighlight(
        "movement_src_payment",
        (saved.mark_paid || [])
          .filter((mp) => mp?.matched && mp.payment_id)
          .map((mp) => mp.payment_id),
      );
      setPendingItems([]);
      setPendingAttachmentId(null);
      setDuplicateWarnings({});
      toast.success(tx(lang, `${counts} movement(s) saved`, `${counts} movimiento(s) guardados`, `${counts} movimento(s) salvos`));
      setPendingAttachmentId(null);
      navigate("/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const suggestions = tx(lang, SUGGESTIONS_EN, SUGGESTIONS_ES, SUGGESTIONS_ES);

  return (
    <Layout>
      <div className="max-w-2xl mx-auto" data-testid="quick-add-page">
        <PageHeader
          testid="quick-add-header"
          eyebrow={tx(lang, "AI assistant", "Asistente IA", "Assistente IA")}
          title={tx(lang, "Lumfi Assistant", "Asistente Lumfi", "Assistente Lumfi")}
          subtitle={tx(lang,
            "Tell it what happened and we'll log it for you.",
            "Cuéntale lo que pasó y lo registramos por ti.",
            "Conta o que aconteceu e registramos para você.",
          )}
          actions={
            <>
              <Link to="/shared?new=1">
                <Button data-testid="open-shared-btn" variant="outline" size="sm" className="gap-2 rounded-xl border-emerald-200 text-emerald-700 hover:bg-emerald-50">
                  <Sparkles className="h-4 w-4" /> {tx(lang, "Share with others", "Compartir con otros", "Partilhar")}
                </Button>
              </Link>
              <Link to="/integrations">
                <Button data-testid="open-integrations-btn" variant="outline" size="sm" className="gap-2 rounded-xl">
                  <MessagesSquare className="h-4 w-4" /> Telegram
                </Button>
              </Link>
              <Button
                data-testid="clear-chat-btn"
                variant="ghost"
                size="sm"
                onClick={clearChat}
                className="gap-2 text-slate-600 hover:text-red-600"
              >
                <MessageSquareDashed className="h-4 w-4" />
                {tx(lang, "Clear chat", "Limpiar chat", "Limpar chat")}
              </Button>
            </>
          }
        />

        {/* Chat area */}
        <Card className="mt-1 rounded-3xl border-slate-200 p-4 sm:p-5 fade-up" data-testid="chat-card">
          <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1" data-testid="chat-messages">
            {messages.map((m, i) => (
              <Bubble
                key={m.id || `m-${i}`}
                role={m.role}
                content={m.content}
                error={m.error}
                quickReplies={m.quick_replies}
                onQuickReply={(text) => send(text)}
                disabled={sending}
              />
            ))}
            {sending && (
              <Bubble role="assistant" content={
                <span className="inline-flex items-center gap-2 text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {tx(lang, "Thinking...", "Analizando...", "Analisando...")}
                </span>
              } />
            )}
            <div ref={endRef} />
          </div>

          {/* Input */}
          <div className="mt-4 flex gap-2 items-end">
            <Textarea
              data-testid="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={tx(lang,
                "Type here… (Enter to send, Shift+Enter for new line)",
                "Escribe aquí… (Enter para enviar, Shift+Enter para nueva línea)",
                "Escreva aqui… (Enter para enviar, Shift+Enter para nova linha)",
              )}
              rows={2}
              className="resize-none rounded-xl"
              disabled={sending}
            />
            <Button
              data-testid="chat-attach-btn"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending || uploadingImage}
              title={tx(lang, "Upload receipt", "Subir comprobante", "Enviar comprovante")}
              variant="outline"
              className="rounded-xl h-[60px] w-[44px] p-0 shrink-0 border-slate-200"
            >
              {uploadingImage
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <Paperclip className="h-4 w-4" />}
            </Button>
            <input /* design-system: ALLOW legacy-todo */
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp,application/pdf"
              data-testid="chat-attach-input"
              className="hidden"
              onChange={(e) => onImagePicked(e.target.files?.[0])}
            />
            <Button
              data-testid="chat-send-btn"
              onClick={() => send()}
              disabled={sending || !input.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 rounded-xl h-[60px] w-[60px] p-0 shrink-0"
            >
              {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
            </Button>
          </div>

          {/* Suggestions */}
          {messages.length <= 1 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="chat-suggestions">
              {suggestions.map((s, i) => (
                <Chip
                  key={s}
                  data-testid={`chat-suggestion-${i}`}
                  onClick={() => send(s)}
                  size="md"
                  className="rounded-full normal-case tracking-normal"
                >
                  {s}
                </Chip>
              ))}
            </div>
          )}
        </Card>

        {/* Pending items review */}
        {pendingItems.length > 0 && (
          <Card className="mt-5 p-5 rounded-3xl border-slate-200 fade-up" data-testid="pending-items">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                <h2 className="font-display text-lg font-bold text-slate-900">
                  {tx(lang, "Pending to save", "Pendientes de guardar", "Pendentes de salvar")} ({pendingItems.length})
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  data-testid="chat-discard-all-btn"
                  variant="ghost"
                  size="sm"
                  onClick={discardAllItems}
                  className="text-slate-500 hover:text-red-600 gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  {tx(lang, "Discard all", "Descartar todos", "Descartar todos")}
                </Button>
                <Button
                  data-testid="chat-save-all-btn"
                  onClick={saveAll}
                  disabled={saving}
                  className="bg-slate-900 hover:bg-slate-800 rounded-xl gap-2"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                  {tx(lang, "Save all", "Guardar todo", "Salvar tudo")}
                </Button>
              </div>
            </div>
            <div className="space-y-3">
              {pendingItems.map((it, idx) => (
                <ItemCard
                  key={it.id || idx}
                  item={it}
                  currency={currency}
                  lang={lang}
                  onChange={(patch) => editItem(idx, patch)}
                  onRemove={() => removeItem(idx)}
                  onSwapType={() => swapItemType(idx)}
                  testid={`pending-item-${idx}`}
                  duplicateWarning={duplicateWarnings[idx]}
                />
              ))}
            </div>
          </Card>
        )}
      </div>
    </Layout>
  );
}

function Bubble({ role, content, error, quickReplies, onQuickReply, disabled }) {
  const isUser = role === "user";
  const chips = Array.isArray(quickReplies) ? quickReplies.filter(Boolean) : [];
  return (
    <div className={`flex flex-col gap-1.5 ${isUser ? "items-end" : "items-start"}`} data-testid={`bubble-${role}`}>
      <div className={`flex gap-2 ${isUser ? "flex-row-reverse" : ""}`}>
        <div
          className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${
            isUser ? "bg-slate-900 text-white" : "bg-emerald-50 text-emerald-700"
          }`}
        >
          {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
        </div>
        <div
          className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
            isUser
              ? "bg-slate-900 text-white rounded-tr-sm"
              : error
              ? "bg-red-50 text-red-800 border border-red-200 rounded-tl-sm"
              : "bg-slate-100 text-slate-900 rounded-tl-sm"
          }`}
        >
          {content}
        </div>
      </div>
      {chips.length > 0 && (
        <div className="flex flex-wrap gap-1.5 ml-10" data-testid="chat-quick-replies">
          {chips.map((c) => (
            <Chip
              key={c}
              data-testid={`chat-quick-reply-${c}`}
              onClick={() => onQuickReply?.(c)}
              disabled={disabled}
              tone="emerald"
              size="md"
              className="bg-white normal-case tracking-normal"
            >
              {c}
            </Chip>
          ))}
        </div>
      )}
    </div>
  );
}

function ItemCard({ item, currency, lang, onChange, onRemove, onSwapType, testid, duplicateWarning }) {
  const labels = {
    income: tx(lang, "Income", "Ingreso", "Receita"),
    expense: tx(lang, "Expense", "Gasto", "Despesa"),
    receivable: tx(lang, "Receivable", "Cobro", "A receber"),
    payment: tx(lang, "Payment", "Pago", "Pagamento"),
  };
  const colors = {
    income: "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100",
    expense: "bg-red-50 text-red-700 ring-red-200 hover:bg-red-100",
    receivable: "bg-blue-50 text-blue-700 ring-blue-200 hover:bg-blue-100",
    payment: "bg-amber-50 text-amber-700 ring-amber-200 hover:bg-amber-100",
  };
  const swapHint = {
    income: tx(lang, "Switch to Expense", "Cambiar a Gasto", "Mudar para Despesa"),
    expense: tx(lang, "Switch to Income", "Cambiar a Ingreso", "Mudar para Receita"),
    receivable: tx(lang, "Switch to Payment", "Cambiar a Pago", "Mudar para Pagamento"),
    payment: tx(lang, "Switch to Receivable", "Cambiar a Cobro", "Mudar para A receber"),
  };

  // iter-169z7 — Inline account picker. Lazy-load the user's real
  // cash accounts and let them choose one without leaving the confirm
  // panel. "Otra…" reveals a free-text input that the backend will
  // fuzzy-match against existing accounts on save.
  const { data: accounts = [] } = useQuery({
    queryKey: ["cash-accounts-picker"],
    queryFn: async () => {
      const resp = (await import("../../lib/api")).api.get("/cash-accounts").then((r) => r.data);
      const data = await resp;
      return Array.isArray(data) ? data : (data?.rows || []);
    },
    staleTime: 60_000,
  });
  // Local UI state: "" → no picker open / pre-AI value; "<id>" → known
  // account; "__other__" → free-text fallback. Initialise based on
  // what the LLM extracted (cash_account_name) or what the user
  // already chose on a previous render.
  const initialSelect = item.cash_account_id
    ? item.cash_account_id
    : item.cash_account_name
      ? "__other__"
      : "";
  const [pickerValue, setPickerValue] = useState(initialSelect);
  const isOther = pickerValue === "__other__";
  const handlePickerChange = (val) => {
    setPickerValue(val);
    if (val === "__other__") {
      // Keep whatever text we already had (from LLM) or start blank.
      onChange({ ...item, cash_account_id: undefined });
    } else if (val) {
      const acct = accounts.find((a) => a.id === val);
      onChange({
        ...item,
        cash_account_id: val,
        cash_account_name: acct?.name || undefined,
      });
    } else {
      onChange({ ...item, cash_account_id: undefined, cash_account_name: undefined });
    }
  };
  const supportsAccount = ["income", "expense", "receivable", "payment"].includes(item.type);
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-slate-200 p-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Chip
            data-testid={`${testid}-swap-type`}
            onClick={onSwapType}
            title={swapHint[item.type]}
            className={`!ring-1 cursor-pointer ${colors[item.type]}`}
          >
            {labels[item.type]}
          </Chip>
          <span className="font-display text-lg font-bold tabular-nums text-slate-900">
            {formatMoney(item.amount, currency)}
          </span>
        </div>
        <IconToggle
          data-testid={`${testid}-remove`}
          onClick={onRemove}
          aria-label="Remove item"
          variant="danger"
          size="md"
        >
          <Trash2 className="h-4 w-4" />
        </IconToggle>
      </div>

      {/* iter-169zv — Duplicate match warning. Surfaces inline so the
          user can drop the item before saving (or keep it knowingly). */}
      {duplicateWarning && (
        <div
          data-testid={`${testid}-duplicate-warning`}
          className={`mt-3 rounded-xl p-2.5 flex items-start gap-2 text-xs ${
            duplicateWarning.strong
              ? "bg-amber-50 border border-amber-300 text-amber-900"
              : "bg-amber-50/60 border border-amber-200 text-amber-800"
          }`}
        >
          <AlertTriangle className={`h-4 w-4 shrink-0 mt-0.5 ${duplicateWarning.strong ? "text-amber-700" : "text-amber-600"}`} />
          <div className="min-w-0 flex-1">
            <div className="font-semibold leading-tight">
              {tx(lang,
                duplicateWarning.strong ? "Possible duplicate" : "Recent similar movement",
                duplicateWarning.strong ? "Posible duplicado" : "Movimiento similar reciente",
                duplicateWarning.strong ? "Possível duplicata" : "Movimento similar recente",
              )}
            </div>
            <div className="text-[11px] leading-snug mt-0.5">
              {tx(lang,
                "You already have a movement of ",
                "Ya tienes un movimiento de ",
                "Você já tem um movimento de ",
              )}
              <span className="font-mono-num font-semibold">
                {formatMoney(duplicateWarning.existing_amount, duplicateWarning.existing_currency || currency)}
              </span>
              {" "}
              {tx(lang, "from", "del", "do")}{" "}
              <span className="font-mono-num">{duplicateWarning.existing_date}</span>
              {duplicateWarning.existing_description ? (
                <>
                  {" · "}
                  <span className="italic truncate inline-block max-w-[160px] align-bottom">
                    {duplicateWarning.existing_description}
                  </span>
                </>
              ) : null}
              {". "}
              {tx(lang,
                "If it's the same, drop this item with the trash icon.",
                "Si es el mismo, descártalo con la papelera.",
                "Se for o mesmo, descarte com o ícone da lixeira.",
              )}
            </div>
          </div>
        </div>
      )}

      {/* iter-169z7 — Inline account picker. Replaces the read-only
          "Sin cuenta" badge: the user can now wire the right account
          *before* confirming, saving the round trip through History. */}
      {supportsAccount && (
        <div className="mt-3 space-y-1.5" data-testid={`${testid}-account-picker`}>
          <label className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500">
            {tx(lang, "Account used", "Cuenta usada", "Conta usada")}
          </label>
          <div className="flex gap-2">
            <select /* design-system: ALLOW legacy-todo */
              value={pickerValue}
              onChange={(e) => handlePickerChange(e.target.value)}
              data-testid={`${testid}-account-select`}
              className="flex-1 text-sm rounded-lg border border-slate-200 px-2 py-1.5 bg-white focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
            >
              <option value="">
                {tx(lang, "— No account —", "— Sin cuenta —", "— Sem conta —")}
              </option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name} {a.currency ? `(${a.currency})` : ""}
                </option>
              ))}
              <option value="__other__">
                {tx(lang, "Other…", "Otra…", "Outra…")}
              </option>
            </select>
            {isOther && (
              <input /* design-system: ALLOW legacy-todo */
                type="text"
                placeholder={tx(lang, "Account name", "Nombre de la cuenta", "Nome da conta")}
                value={item.cash_account_name || ""}
                onChange={(e) => onChange({ ...item, cash_account_name: e.target.value })}
                data-testid={`${testid}-account-other-input`}
                className="flex-1 text-sm rounded-lg border border-slate-200 px-2 py-1.5 focus:border-emerald-400 focus:ring-1 focus:ring-emerald-200 outline-none"
              />
            )}
          </div>
          {pickerValue === "" && (
            <p className="text-[11px] text-amber-700">
              {tx(lang,
                "No account — the movement will use your primary account.",
                "Sin cuenta — el movimiento afectará tu cuenta primaria.",
                "Sem conta — o movimento usará sua conta principal.",
              )}
            </p>
          )}
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm">
        {/* Amount editor */}
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
            {tx(lang, "Amount", "Monto", "Valor")}
          </div>
          <Input
            data-testid={`${testid}-amount`}
            type="number"
            value={item.amount}
            onChange={(e) => onChange({ amount: Number(e.target.value) })}
            className="h-9 mt-0.5"
          />
        </div>
        {(item.type === "income" || item.type === "expense") && (
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              {tx(lang, "Description", "Descripción", "Descrição")}
            </div>
            <Input
              data-testid={`${testid}-description`}
              value={item.description || ""}
              onChange={(e) => onChange({ description: e.target.value })}
              className="h-9 mt-0.5"
            />
          </div>
        )}
        {item.type === "receivable" && (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                {tx(lang, "Client", "Cliente", "Cliente")}
              </div>
              <Input
                data-testid={`${testid}-client`}
                value={item.client_name || ""}
                onChange={(e) => onChange({ client_name: e.target.value })}
                className="h-9 mt-0.5"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                {tx(lang, "Expected date", "Fecha esperada", "Data esperada")}
              </div>
              <Input
                data-testid={`${testid}-date`}
                type="date"
                value={(item.expected_date || "").slice(0, 10)}
                onChange={(e) => onChange({ expected_date: e.target.value })}
                className="h-9 mt-0.5"
              />
            </div>
          </>
        )}
        {item.type === "payment" && (
          <>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                {tx(lang, "Concept", "Concepto", "Conceito")}
              </div>
              <Input
                data-testid={`${testid}-concept`}
                value={item.concept || ""}
                onChange={(e) => onChange({ concept: e.target.value })}
                className="h-9 mt-0.5"
              />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
                {tx(lang, "Due date", "Fecha límite", "Data limite")}
              </div>
              <Input
                data-testid={`${testid}-date`}
                type="date"
                value={(item.due_date || "").slice(0, 10)}
                onChange={(e) => onChange({ due_date: e.target.value })}
                className="h-9 mt-0.5"
              />
            </div>
          </>
        )}
      </div>
      {Array.isArray(item.line_items) && item.line_items.length > 0 && (() => {
        // iter-169z8 — When the LLM extracted line items, surface a
        // chooser if the parent amount disagrees with the items sum so
        // the user picks which value to persist. The OCR can read the
        // grand total directly or compute it; receipts with tip/IVA
        // commonly drift, so we never auto-correct silently.
        const sum = item.line_items.reduce(
          (a, it) => a + (it.subtotal != null ? Number(it.subtotal) : Number(it.qty || 1) * Number(it.price || 0)),
          0,
        );
        const parent = Number(item.amount || 0);
        const diff = Math.abs(parent - sum);
        const showChooser = diff > 1 && Math.abs(diff / Math.max(parent, 1)) > 0.005;
        return (
          <>
            {showChooser && (
              <div
                className="mt-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs"
                data-testid={`${testid}-amount-chooser`}
              >
                <div className="font-semibold text-amber-900 mb-1.5">
                  {tx(lang, "⚠ Declared total ≠ items sum", "⚠ El total declarado no coincide con la suma de ítems", "⚠ Total declarado ≠ soma dos itens")}
                </div>
                <div className="flex flex-wrap gap-2">
                  <button /* design-system: ALLOW legacy-todo */
                    type="button"
                    onClick={() => onChange({ ...item, amount: parent })}
                    className={`flex-1 min-w-[120px] text-left px-3 py-2 rounded-lg border ${
                      Math.abs(parent - item.amount) < 0.01
                        ? "border-emerald-500 bg-white shadow-sm"
                        : "border-slate-200 bg-white/60 hover:border-slate-400"
                    }`}
                    data-testid={`${testid}-pick-declared`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      {tx(lang, "Declared total", "Total declarado", "Total declarado")}
                    </div>
                    <div className="font-mono-num font-bold text-slate-900">{formatMoney(parent, currency)}</div>
                  </button>
                  <button /* design-system: ALLOW legacy-todo */
                    type="button"
                    onClick={() => onChange({ ...item, amount: sum })}
                    className={`flex-1 min-w-[120px] text-left px-3 py-2 rounded-lg border ${
                      Math.abs(sum - item.amount) < 0.01
                        ? "border-emerald-500 bg-white shadow-sm"
                        : "border-slate-200 bg-white/60 hover:border-slate-400"
                    }`}
                    data-testid={`${testid}-pick-sum`}
                  >
                    <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                      {tx(lang, "Items sum", "Suma de ítems", "Soma dos itens")}
                    </div>
                    <div className="font-mono-num font-bold text-slate-900">{formatMoney(sum, currency)}</div>
                  </button>
                </div>
              </div>
            )}
            <LineItemsView
              items={item.line_items}
              amount={item.amount}
              currency={currency}
              reconciled={item.amount_reconciled}
              claimedAmount={item.claimed_amount}
              testid={`${testid}-items`}
            />
          </>
        );
      })()}
    </div>
  );
}
